import { FC, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import React from 'react';

import { useLocalSearchParams, useRouter } from 'expo-router';
import { useLinkWalmartAccount } from 'features/partners-walmart/hooks/use-link-walmart-account';
import { useWalmartEligibility } from 'features/partners-walmart/hooks/use-walmart-eligibility';
import { useWalmartPartnerCode } from 'features/partners-walmart/hooks/use-walmart-partner-code';
import { Linking } from 'react-native';

import DrawerPage from 'components/layout/drawer-page';
import { useIsDesktopWebBreakpoint, useIsMobileBreakpoint } from 'hooks/use-media-query';
import { useAuthContext } from 'state/auth';
import { ModalAuthScreen, ModalAuthTrigger } from 'state/auth/types';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLaunchDarklyClientReady } from 'state/launchdarkly/use-launch-darkly-client';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import {
  PARTNER_USER_ALREADY_LINKED,
  WALMART_PLUS_URL,
  isActiveWalmartUser,
} from 'state/loyalty/hooks/utils/walmart';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { EligibilityErrorModal, WalmartEligibilityErrors } from './eligibility-error-modal';
import { getWalmartLandingState } from './landing-state';
import { useLoyaltyWalmart } from './use-loyalty-walmart';
import { WalmartFooter } from './walmart-footer';
import { WalmartHeaderV2 } from './walmart-header-v2';
import { WalmartLandingContent } from './walmart-landing-content';
import { WalmartWelcome } from './walmart-welcome';
import { WalmartWelcomeBack } from './walmart-welcome-back';

interface WalmartLandingAttemptProps {
  activeAuthz?: string;
  authzCode?: string | string[];
}

const WalmartLandingAttempt: FC<WalmartLandingAttemptProps> = ({ activeAuthz, authzCode }) => {
  const router = useRouter();
  const attemptIsActive = useRef(true);

  useLayoutEffect(() => {
    attemptIsActive.current = true;
    return () => {
      attemptIsActive.current = false;
    };
  }, []);

  const {
    loyaltyUser,
    loading: loyaltyUserLoading,
    refetch: refetchLoyaltyUser,
  } = useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const { setUserEmail, userEmail } = useUIContext();

  const {
    isAuthenticated,
    setModalAuthState,
    signOut,
    setAlertMessage: setAuthModalInputAlert,
    user,
    loading: authUserLoading,
  } = useAuthContext();

  const [openWelcomeDrawer, setOpenWelcomeDrawer] = useState<boolean>(false);
  const [openWelcomeBackDrawer, setOpenWelcomeBackDrawer] = useState<boolean>(false);
  const [shouldLink, setShouldLink] = useState<boolean>(false);
  const [shouldRedirect, setShouldRedirect] = useState<boolean>(false);
  const { data: walmartInfo, loading: walmartInfoLoading } = useLoyaltyWalmart();

  const {
    called: eligibilityCalled,
    data: eligibility,
    error: eligibilityError,
    getEligibility,
    loading: eligibilityLoading,
  } = useWalmartEligibility(activeAuthz);

  const { getPartnerCode } = useWalmartPartnerCode(loyaltyUser?.id);

  const {
    linkWalmartAccount,
    loading: linkLoading,
    data: linkData,
    called: linkCalled,
  } = useLinkWalmartAccount();

  const [eligibilityErrorCode, setEligibilityErrorCode] = useState<WalmartEligibilityErrors>();

  const enableWalmartPlusAnnualTrialOffer = useFlag(
    LaunchDarklyFlag.ENABLE_WALMART_PLUS_TRIAL_OFFER
  );

  const enableWalmartPlusMonthlyTrialOffer = useFlag(
    LaunchDarklyFlag.ENABLE_WALMART_PLUS_TRIAL_OFFER_MONTHLY
  );

  const enableWalmartPlusTrialOffer =
    enableWalmartPlusAnnualTrialOffer || enableWalmartPlusMonthlyTrialOffer;

  const isAlreadyLinked = isActiveWalmartUser(loyaltyUser);
  const landingState = getWalmartLandingState(isAuthenticated, isAlreadyLinked);
  const ldClientReady = useLaunchDarklyClientReady();

  // Trial-offer flags default to false until LaunchDarkly resolves. Keep the link CTA loading
  // until then so it cannot open the standard Walmart URL instead of the promotional one.
  const isLoading =
    !ldClientReady ||
    authUserLoading ||
    loyaltyUserLoading ||
    eligibilityLoading ||
    linkLoading ||
    walmartInfoLoading;

  // `loyaltyUserLoading` alone can't account for auth still being in flight (no loyaltyId is
  // known yet), so it's combined with `authUserLoading` here.
  const isLoyaltyUserResolved = !authUserLoading && !loyaltyUserLoading;

  const handleRedirectToLandingPage = () => {
    setEligibilityErrorCode(undefined);
    router.navigate(routes.walmart);
  };

  const handleRedirectToWalmartPlus = useCallback(async () => {
    // Footer, welcome confirmation, and eligibility "Try Again" all use this handler. Ignore
    // those presses until LaunchDarkly resolves so a false trial-flag default cannot open the
    // standard Walmart URL.
    if (!ldClientReady) {
      return;
    }

    let walmartUrl = walmartInfo?.walmartPlusUrl;

    if (enableWalmartPlusTrialOffer) {
      const walmartCode = await getPartnerCode();
      if (!attemptIsActive.current) {
        return;
      }
      // If partner promo code and url is set, we use that
      walmartUrl =
        walmartCode && walmartInfo?.walmartPromotionUrl
          ? `${walmartInfo.walmartPromotionUrl}${walmartCode}`
          : walmartUrl;
    }

    setEligibilityErrorCode(undefined);
    Linking.openURL(walmartUrl || WALMART_PLUS_URL);
  }, [
    enableWalmartPlusTrialOffer,
    getPartnerCode,
    ldClientReady,
    walmartInfo?.walmartPlusUrl,
    walmartInfo?.walmartPromotionUrl,
  ]);

  const handleRedirectToOffersPage = useCallback(() => {
    setEligibilityErrorCode(undefined);
    router.dismissAll();

    if (eligibility && !eligibility.isExistingUser) {
      return router.navigate(routes.walmartLocation);
    }
    return router.navigate(`${routes.loyaltyOfferList}?success=true`);
  }, [router, eligibility]);

  const checkEligibility = useCallback(async () => {
    try {
      if (!activeAuthz) {
        return setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
      }
      if (
        !isAlreadyLinked &&
        !eligibilityCalled &&
        !eligibilityLoading &&
        !eligibility &&
        !eligibilityError
      ) {
        const { data, error } = await getEligibility({
          authz: activeAuthz,
          loyaltyId: user?.loyaltyId || undefined,
        });
        if (!attemptIsActive.current) {
          return;
        }
        if (error) {
          setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
          return;
        }
        if (data) {
          const { eligible, notEligibleReason, email } = data;

          if (!eligible && notEligibleReason === PARTNER_USER_ALREADY_LINKED) {
            // Corner case when linked W+ member somehow makes it to landing page with authz code
            // The W+ user is already linked to BK account ${data.linkedAccountEmail}.
            // This should be extremely rare outside of testing purposes, since W+ users will
            // not be able to attempt relink on an account they already linked`
            if (!isAuthenticated) {
              return setOpenWelcomeBackDrawer(true);
            }
          }

          if (!eligible) {
            return setEligibilityErrorCode(
              (notEligibleReason as WalmartEligibilityErrors) || WalmartEligibilityErrors.DEFAULT
            );
          }
          if (!isAuthenticated) {
            setUserEmail(email);
          }
        }
      }
    } catch (err) {
      return setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
    }
  }, [
    activeAuthz,
    isAlreadyLinked,
    eligibilityCalled,
    eligibilityLoading,
    eligibility,
    eligibilityError,
    getEligibility,
    user?.loyaltyId,
    isAuthenticated,
    setUserEmail,
  ]);

  const switchAccount = useCallback(async () => {
    setOpenWelcomeBackDrawer(false);
    if (isAuthenticated) {
      await signOut();
    }
    if (!attemptIsActive.current) {
      return;
    }
    setUserEmail(eligibility?.email || '');
    setModalAuthState({
      screen: ModalAuthScreen.SIGN_IN,
      trigger: ModalAuthTrigger.Global,
      user: {
        name: eligibility?.firstName || '',
      },
      onSignInSuccess: async () => {
        if (attemptIsActive.current) {
          setShouldLink(true);
        }
      },
    });
  }, [eligibility, isAuthenticated, setModalAuthState, setUserEmail, signOut]);

  const linkAccount = useCallback(async () => {
    if (!eligibility) {
      return setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
    }

    if (eligibility.isExistingUser && !isAuthenticated) {
      return switchAccount();
    }

    setOpenWelcomeBackDrawer(false);

    try {
      const { data, errors } = await linkWalmartAccount({
        accessToken: eligibility.accessToken,
        refreshToken: eligibility.refreshToken,
        loyaltyId: user?.loyaltyId || '',
      });

      if (!attemptIsActive.current) {
        return;
      }

      if (errors) {
        setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
      } else if (data) {
        const { success, failureReason } = data;
        if (success) {
          await refetchLoyaltyUser();
          if (!attemptIsActive.current) {
            return;
          }
          router.setParams({ success: 'true' });
          handleRedirectToOffersPage();
        } else {
          setEligibilityErrorCode(
            (failureReason as WalmartEligibilityErrors) || WalmartEligibilityErrors.DEFAULT
          );
        }
      }
    } catch {
      if (attemptIsActive.current) {
        setEligibilityErrorCode(WalmartEligibilityErrors.DEFAULT);
      }
    }
  }, [
    eligibility,
    handleRedirectToOffersPage,
    isAuthenticated,
    linkWalmartAccount,
    refetchLoyaltyUser,
    router,
    switchAccount,
    user?.loyaltyId,
  ]);

  // Check eligibility once user data stops loading
  useEffect(() => {
    if (authzCode && !loyaltyUserLoading && !authUserLoading && !eligibilityCalled) {
      if (!isAlreadyLinked) {
        checkEligibility();
      } else {
        setEligibilityErrorCode(WalmartEligibilityErrors.LOYALTY_ID_ALREADY_LINKED);
      }
    }
  }, [
    loyaltyUserLoading,
    authUserLoading,
    checkEligibility,
    eligibilityCalled,
    isAlreadyLinked,
    authzCode,
  ]);

  // Perform link after user switches accounts
  useEffect(() => {
    if (shouldLink && user && !authUserLoading && !isAlreadyLinked && !linkCalled) {
      linkAccount();
    }
  }, [authUserLoading, shouldLink, linkAccount, linkCalled, user, loyaltyUser, isAlreadyLinked]);

  useEffect(() => {
    if (
      shouldRedirect &&
      user &&
      loyaltyUser &&
      !loyaltyUserLoading &&
      !authUserLoading &&
      !isAlreadyLinked
    ) {
      return setOpenWelcomeDrawer(true);
    }
  }, [authUserLoading, isAlreadyLinked, loyaltyUser, loyaltyUserLoading, shouldRedirect, user]);

  useEffect(() => {
    if (userEmail) {
      setAuthModalInputAlert(t('useYourWalmartPlusAccount'));
    }
    return () => setAuthModalInputAlert('');
  }, [setAuthModalInputAlert, userEmail]);

  const handleRedeemOffers = () => {
    const loyaltyLinkWalmartAccountSuccess = !!linkData?.success;

    if (loyaltyLinkWalmartAccountSuccess) {
      router.setParams({ success: 'true' });
      router.navigate(`${routes.loyaltyOfferList}?success=true`);
    } else {
      router.dismissAll();
      router.navigate(routes.loyaltyOfferList);
    }
  };

  const isDesktopWeb = useIsDesktopWebBreakpoint();
  const isMobile = useIsMobileBreakpoint();

  return (
    <DrawerPage
      // TODO the contents of this screen aren't responsive in this manner, easy fix.
      disableWebMaxWidth={isDesktopWeb}
      fullWidthChildren={isMobile}
      contentBackgroundColor="#F5EBDC"
      _headerOverride={<WalmartHeaderV2 />}
      footer={
        <WalmartFooter
          isLoading={isLoading}
          isAlreadyLinked={isAlreadyLinked}
          authzCode={authzCode}
          eligibility={eligibility}
          linkCalled={linkCalled}
          handleShouldRedirect={setShouldRedirect}
          handleOpenWelcomeBackDrawer={setOpenWelcomeBackDrawer}
          handleShouldLink={setShouldLink}
          handleRedirectToWalmartPlus={handleRedirectToWalmartPlus}
          handleRedeemOffers={handleRedeemOffers}
        />
      }
    >
      {/* Starts Walmart Landing */}
      <WalmartLandingContent
        isLoyaltyUserResolved={isLoyaltyUserResolved}
        landingState={landingState}
        handleShouldRedirect={setShouldRedirect}
      />
      {/* Ends Walmart Landing */}

      <WalmartWelcome
        isOpen={openWelcomeDrawer}
        handleOnConfirm={() => handleRedirectToWalmartPlus()}
        handleOnClose={() => setOpenWelcomeDrawer(false)}
      />

      <WalmartWelcomeBack
        isAlreadyLinked={
          !!eligibility && eligibility.notEligibleReason === PARTNER_USER_ALREADY_LINKED
        }
        isOpen={openWelcomeBackDrawer}
        handleOnClose={() => setOpenWelcomeBackDrawer(false)}
        accountEmail={user?.details?.email || userEmail || undefined}
        handleLinkAccount={() => linkAccount()}
        handleSwitchAccount={() => switchAccount()}
        handleSignIn={() => {
          setUserEmail(eligibility?.linkedAccountEmail || '');
          setOpenWelcomeBackDrawer(false);
          return setModalAuthState({
            screen: ModalAuthScreen.SIGN_IN,
            trigger: ModalAuthTrigger.Global,
            onSignInSuccess: async () => {
              if (attemptIsActive.current) {
                router.navigate(`${routes.loyaltyOfferList}?success=true`);
              }
            },
          });
        }}
      />

      <EligibilityErrorModal
        errorCode={eligibilityErrorCode}
        onTryAgain={handleRedirectToWalmartPlus}
        onDismiss={handleRedirectToLandingPage}
        user={user}
        eligibility={eligibility}
      />
    </DrawerPage>
  );
};

export const WalmartLandingPage: FC<React.PropsWithChildren<{}>> = () => {
  const params = useLocalSearchParams();
  // TODO: Fix ?code URL param conflict with OTP form
  const authzCode = params.authz || params.code || params.AuthZCode;
  const activeAuthz = typeof authzCode === 'string' ? authzCode : undefined;

  return (
    <WalmartLandingAttempt
      key={activeAuthz ?? 'walmart-landing-without-authz'}
      activeAuthz={activeAuthz}
      authzCode={authzCode}
    />
  );
};
